import React, { useEffect, useRef, useState } from "react";
import { Rnd } from "react-rnd";
import { api, formatApiError } from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import {
  UploadSimple, FloppyDisk, ArrowsClockwise, Image as ImageIcon, X,
  Eye, EyeSlash, ArrowsOutCardinal, TextAa, FrameCorners
} from "@phosphor-icons/react";
import { CARD_W, CARD_H, DEFAULT_LAYOUT, ELEMENT_LABELS, resolveLayout, resolveTextContent } from "@/lib/cardLayout";

const DEFAULT_CFG = {
  club_name: "Entradas Deportivas",
  card_subtitle: "Carné Digital",
  primary_color: "#007AFF",
  secondary_color: "#0A2D5C",
  card_text_color: "#FFFFFF",
  logo_url: null,
  secondary_logo_url: null,
  background_image_url: null,
  background_image_only: false,
  show_photo: true,
  show_dni: true,
  card_layout: null,
};

const PREVIEW_SOCIO = {
  qr_code: "SOCIO-PREVIEW-1234",
  numero_socio: "001",
  nombre: "Juan",
  apellidos: "García Pérez",
  dni: "12345678A",
  temporada: "2026-27",
  foto_url: null,
};

// Convierte un File a data-URL base64 (para guardar inline en la config sin auth)
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function ConfigClub() {
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [layout, setLayout] = useState(resolveLayout(null));
  const [selected, setSelected] = useState("club_name");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canvasScale, setCanvasScale] = useState(1);
  const canvasWrapperRef = useRef(null);
  const logoRef = useRef(null);
  const logo2Ref = useRef(null);
  const bgRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/club-config");
        setCfg({ ...DEFAULT_CFG, ...data });
        setLayout(resolveLayout(data?.card_layout));
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Ajusta el zoom del canvas al ancho disponible
  useEffect(() => {
    const compute = () => {
      const w = canvasWrapperRef.current?.clientWidth || CARD_W;
      const s = Math.min(1, (w - 24) / CARD_W);
      setCanvasScale(s);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [loading]);

  const updateEl = (key, patch) => {
    setLayout((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const pickImage = async (file, key) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen no debe superar 2 MB");
      return;
    }
    const dataUrl = await fileToBase64(file);
    setCfg((c) => ({ ...c, [key]: dataUrl }));
    toast.success("Imagen cargada");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/club-config", { ...cfg, card_layout: layout });
      setCfg({ ...DEFAULT_CFG, ...data });
      setLayout(resolveLayout(data?.card_layout));
      toast.success("Diseño guardado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (!window.confirm("¿Restaurar diseño y colores por defecto?")) return;
    setCfg(DEFAULT_CFG);
    setLayout(resolveLayout(null));
    toast.success("Diseño restaurado (recuerda guardar)");
  };

  const resetLayoutOnly = () => {
    setLayout(resolveLayout(null));
    toast.success("Posiciones restauradas");
  };

  if (loading) return <div className="text-slate-500">Cargando...</div>;

  const sel = layout[selected];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#2563EB] font-bold">Configuración</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tighter">Diseño del Carné</h1>
          <p className="text-sm text-slate-500 mt-1">Arrastra, redimensiona y personaliza los elementos del carné. El diseño se aplica a todos los socios.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={resetLayoutOnly} className="px-4 h-11 border border-slate-200 hover:bg-slate-50 text-sm font-display font-bold uppercase tracking-wide flex items-center gap-2" style={{ borderRadius: 8 }}>
            <ArrowsClockwise size={16} /> Reset posiciones
          </button>
          <button onClick={reset} className="px-4 h-11 border border-slate-200 hover:bg-slate-50 text-sm font-display font-bold uppercase tracking-wide flex items-center gap-2" style={{ borderRadius: 8 }}>
            <ArrowsClockwise size={16} /> Restaurar todo
          </button>
          <button
            data-testid="save-club-config"
            onClick={save}
            disabled={saving}
            className="px-5 h-11 bg-[#2563EB] hover:bg-[#1d4ed8] text-white text-sm font-display font-bold uppercase tracking-wide flex items-center gap-2 disabled:opacity-50 shadow-md shadow-blue-500/20"
            style={{ borderRadius: 8 }}
          >
            <FloppyDisk size={16} weight="bold" /> {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* === DESIGNER CANVAS === */}
        <div>
          <div ref={canvasWrapperRef} className="bg-white border border-slate-200 shadow-card p-4" style={{ borderRadius: 12 }}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">Vista del carné · arrastra para mover, esquinas para redimensionar</div>
            </div>
            <DesignerCanvas
              cfg={cfg}
              layout={layout}
              selected={selected}
              setSelected={setSelected}
              updateEl={updateEl}
              scale={canvasScale}
            />
          </div>

          {/* Background image */}
          <div className="bg-white border border-slate-200 shadow-card p-5 mt-4" style={{ borderRadius: 12 }}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-display font-bold text-lg uppercase tracking-tight">Fondo del carné</h3>
                <p className="text-xs text-slate-500">
                  {cfg.background_image_url
                    ? (cfg.background_image_only
                        ? "Se muestra la imagen tal cual, sin ningún color por encima."
                        : "Se aplica un tinte con los colores primario y secundario sobre la imagen.")
                    : "Sin imagen: se usa el degradado con los colores del club."}
                </p>
              </div>
              <div className="flex gap-2 items-center">
                {cfg.background_image_url && (
                  <button
                    onClick={() => setCfg({ ...cfg, background_image_url: null, background_image_only: false })}
                    className="h-9 px-3 text-xs border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"
                    style={{ borderRadius: 8 }}
                    data-testid="remove-bg-btn"
                  >
                    <X size={14} /> Quitar
                  </button>
                )}
                <button
                  onClick={() => bgRef.current?.click()}
                  data-testid="upload-bg"
                  className="h-9 px-4 text-xs bg-slate-900 text-white hover:bg-slate-700 font-bold uppercase tracking-wider flex items-center gap-1.5"
                  style={{ borderRadius: 8 }}
                >
                  <UploadSimple size={14} /> Subir imagen
                </button>
                <input ref={bgRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => e.target.files?.[0] && pickImage(e.target.files[0], "background_image_url")} />
              </div>
            </div>

            {cfg.background_image_url && (
              <label
                className="mt-4 flex items-start gap-3 p-3 border border-slate-200 hover:border-[#2563EB] cursor-pointer transition"
                style={{ borderRadius: 8 }}
                data-testid="bg-image-only-toggle"
              >
                <input
                  type="checkbox"
                  checked={!!cfg.background_image_only}
                  onChange={(e) => setCfg({ ...cfg, background_image_only: e.target.checked })}
                  className="mt-0.5 h-4 w-4 accent-[#2563EB]"
                  data-testid="bg-image-only-checkbox"
                />
                <div>
                  <div className="text-sm font-bold text-slate-800">Mostrar la imagen tal cual (sin superposición de color)</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Ideal cuando la imagen ya está diseñada. Recomendamos ajustar el color del texto en el panel de la derecha para que contraste con la imagen.
                  </div>
                </div>
              </label>
            )}
          </div>
        </div>

        {/* === INSPECTOR PANEL === */}
        <div className="space-y-4 lg:sticky lg:top-24 h-fit">
          {/* Element list */}
          <Section title="Elementos">
            <div className="space-y-1 max-h-72 overflow-y-auto -mr-1 pr-1">
              {Object.entries(layout).map(([key, el]) => (
                <button
                  key={key}
                  onClick={() => setSelected(key)}
                  data-testid={`elem-${key}`}
                  className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition ${
                    selected === key ? "bg-[#2563EB] text-white" : "hover:bg-slate-50 text-slate-700"
                  }`}
                  style={{ borderRadius: 8 }}
                >
                  <span className="truncate">{ELEMENT_LABELS[key]}</span>
                  <span
                    role="button"
                    onClick={(e) => { e.stopPropagation(); updateEl(key, { visible: !el.visible }); }}
                    className={`shrink-0 ml-2 cursor-pointer ${selected === key ? "text-white/80" : "text-slate-400 hover:text-slate-700"}`}
                    title={el.visible ? "Ocultar" : "Mostrar"}
                  >
                    {el.visible ? <Eye size={16} /> : <EyeSlash size={16} />}
                  </span>
                </button>
              ))}
            </div>
          </Section>

          {/* Selected element controls */}
          {sel && (
            <Section title={`Editar: ${ELEMENT_LABELS[selected]}`} icon={ArrowsOutCardinal}>
              <Toggle label="Visible" checked={sel.visible} onChange={(v) => updateEl(selected, { visible: v })} />

              <div className="grid grid-cols-2 gap-2 mt-3">
                <NumberField label="X" value={Math.round(sel.x)} onChange={(v) => updateEl(selected, { x: v })} />
                <NumberField label="Y" value={Math.round(sel.y)} onChange={(v) => updateEl(selected, { y: v })} />
              </div>

              {sel.type === "text" && (
                <div className="mt-3">
                  <SliderField icon={TextAa} label="Tamaño de texto" value={sel.fontSize} min={8} max={80} onChange={(v) => updateEl(selected, { fontSize: v })} />
                </div>
              )}
              {(sel.type === "image" || sel.type === "photo") && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <SliderField icon={FrameCorners} label="Ancho" value={sel.width} min={20} max={CARD_W} onChange={(v) => updateEl(selected, { width: v })} />
                  <SliderField icon={FrameCorners} label="Alto" value={sel.height} min={20} max={CARD_H} onChange={(v) => updateEl(selected, { height: v })} />
                </div>
              )}
              {sel.type === "qr" && (
                <div className="mt-3">
                  <SliderField icon={FrameCorners} label="Tamaño del QR" value={sel.size} min={40} max={260} onChange={(v) => updateEl(selected, { size: v })} />
                </div>
              )}
            </Section>
          )}

          {/* Brand & colors */}
          <Section title="Textos">
            <div className="space-y-3">
              <Field label="Nombre del club">
                <input data-testid="cfg-club-name" value={cfg.club_name} onChange={(e) => setCfg({ ...cfg, club_name: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Subtítulo">
                <input data-testid="cfg-card-subtitle" value={cfg.card_subtitle} onChange={(e) => setCfg({ ...cfg, card_subtitle: e.target.value })} className={inputCls} />
              </Field>
            </div>
          </Section>

          <Section title="Colores">
            <div className="grid grid-cols-2 gap-2">
              <ColorPicker label="Primario" value={cfg.primary_color} onChange={(v) => setCfg({ ...cfg, primary_color: v })} testid="cfg-primary" />
              <ColorPicker label="Secundario" value={cfg.secondary_color} onChange={(v) => setCfg({ ...cfg, secondary_color: v })} testid="cfg-secondary" />
              <ColorPicker label="Texto" value={cfg.card_text_color} onChange={(v) => setCfg({ ...cfg, card_text_color: v })} testid="cfg-text" />
            </div>
          </Section>

          <Section title="Logos">
            <div className="grid grid-cols-2 gap-2">
              <LogoSlot label="Logo principal" url={cfg.logo_url} onPick={() => logoRef.current?.click()} onRemove={() => setCfg({ ...cfg, logo_url: null })} testid="logo-main" />
              <LogoSlot label="Logo secundario" url={cfg.secondary_logo_url} onPick={() => logo2Ref.current?.click()} onRemove={() => setCfg({ ...cfg, secondary_logo_url: null })} testid="logo-secondary" />
              <input ref={logoRef} data-testid="upload-logo-main" type="file" accept="image/*" className="hidden"
                onChange={(e) => e.target.files?.[0] && pickImage(e.target.files[0], "logo_url")} />
              <input ref={logo2Ref} data-testid="upload-logo-secondary" type="file" accept="image/*" className="hidden"
                onChange={(e) => e.target.files?.[0] && pickImage(e.target.files[0], "secondary_logo_url")} />
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

/* ============================================================ */
/* CANVAS                                                       */
/* ============================================================ */
function DesignerCanvas({ cfg, layout, selected, setSelected, updateEl, scale }) {
  const bgStyle = cfg.background_image_url
    ? { background: `url(${cfg.background_image_url}) center/cover no-repeat` }
    : { background: `linear-gradient(140deg, ${cfg.primary_color} 0%, ${cfg.secondary_color} 60%, #0A0A0A 100%)` };

  return (
    <div
      onClick={() => setSelected(null)}
      style={{
        position: "relative",
        width: CARD_W * scale,
        height: CARD_H * scale,
        margin: "0 auto",
        ...bgStyle,
        borderRadius: 16 * scale,
        overflow: "hidden",
        color: cfg.card_text_color,
        boxShadow: "0 24px 60px rgba(15, 23, 42, 0.25)",
        fontFamily: "'Barlow Condensed', 'DM Sans', sans-serif",
      }}
    >
      {cfg.background_image_url && !cfg.background_image_only && (
        <div style={{
          position: "absolute", inset: 0,
          background: `linear-gradient(140deg, ${cfg.primary_color}55 0%, ${cfg.secondary_color}aa 100%)`,
          pointerEvents: "none",
        }} />
      )}

      {Object.entries(layout).map(([key, el]) => (
        <DraggableElement
          key={key}
          elementKey={key}
          el={el}
          cfg={cfg}
          scale={scale}
          isSelected={selected === key}
          onSelect={() => setSelected(key)}
          onChange={(patch) => updateEl(key, patch)}
        />
      ))}
    </div>
  );
}

function DraggableElement({ elementKey, el, cfg, scale, isSelected, onSelect, onChange }) {
  if (!el.visible) return null;

  const isText = el.type === "text";
  const isImage = el.type === "image";
  const isPhoto = el.type === "photo";
  const isQr = el.type === "qr";

  const width = isQr ? el.size : el.width;
  const height = isQr ? el.size : el.height;

  // Text auto-sizes — we estimate width based on font size to give Rnd a workable box
  const text = resolveTextContent(el, PREVIEW_SOCIO, cfg);
  const estWidth = isText ? Math.max(60, text.length * el.fontSize * 0.55) : width;
  const estHeight = isText ? el.fontSize * 1.2 : height;

  return (
    <Rnd
      size={{ width: estWidth * scale, height: estHeight * scale }}
      position={{ x: el.x * scale, y: el.y * scale }}
      onDragStop={(e, d) => onChange({ x: d.x / scale, y: d.y / scale })}
      onResizeStop={(e, dir, ref, delta, pos) => {
        const newW = ref.offsetWidth / scale;
        const newH = ref.offsetHeight / scale;
        if (isText) {
          // For text, ignore manual resize — font slider controls size
          onChange({ x: pos.x / scale, y: pos.y / scale });
        } else if (isQr) {
          onChange({ size: Math.round(Math.min(newW, newH)), x: pos.x / scale, y: pos.y / scale });
        } else {
          onChange({ width: Math.round(newW), height: Math.round(newH), x: pos.x / scale, y: pos.y / scale });
        }
      }}
      enableResizing={!isText ? { bottomRight: true, bottomLeft: true, topRight: true, topLeft: true } : false}
      bounds="parent"
      style={{
        zIndex: isSelected ? 100 : (el.z || 4),
        outline: isSelected ? "2px solid #2563EB" : "2px solid transparent",
        outlineOffset: 1,
        cursor: "move",
      }}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
    >
      <div style={{ width: "100%", height: "100%", pointerEvents: "auto" }}>
        <RenderInner el={el} elementKey={elementKey} cfg={cfg} scale={scale} text={text} />
      </div>
    </Rnd>
  );
}

function RenderInner({ el, elementKey, cfg, scale, text }) {
  if (el.type === "image") {
    const url = elementKey === "logo" ? cfg.logo_url : cfg.secondary_logo_url;
    if (!url) {
      return (
        <div style={{
          width: "100%", height: "100%",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(255,255,255,0.08)", border: "2px dashed rgba(255,255,255,0.3)",
          borderRadius: 8 * scale, fontSize: 11 * scale, letterSpacing: 2, fontWeight: 700,
        }}>
          {elementKey === "logo" ? "LOGO" : "LOGO 2"}
        </div>
      );
    }
    return <img src={url} alt="" draggable={false}
      style={{ width: "100%", height: "100%", objectFit: "contain", background: "rgba(255,255,255,0.08)", padding: 4 * scale, borderRadius: 8 * scale, pointerEvents: "none" }} />;
  }

  if (el.type === "photo") {
    return (
      <div style={{
        width: "100%", height: "100%",
        background: "rgba(255,255,255,0.08)", border: "2px dashed rgba(255,255,255,0.3)",
        borderRadius: 8 * scale, display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 11 * scale, fontWeight: 700, letterSpacing: 2,
      }}>FOTO</div>
    );
  }

  if (el.type === "qr") {
    const displaySize = el.size * scale;
    const renderSize = Math.round(displaySize * 4);
    return (
      <div style={{ width: "100%", height: "100%", background: "#fff", padding: 6 * scale, borderRadius: 6 * scale, lineHeight: 0, pointerEvents: "none" }}>
        <QRCodeCanvas
          value="SOCIO-PREVIEW-1234"
          size={renderSize}
          level="H"
          includeMargin={false}
          style={{ width: displaySize - 12 * scale, height: displaySize - 12 * scale, display: "block" }}
        />
      </div>
    );
  }

  return (
    <div style={{
      fontSize: el.fontSize * scale,
      fontWeight: el.weight || 500,
      textAlign: el.align || "left",
      letterSpacing: el.letterSpacing ? el.letterSpacing * scale * 0.4 : 0,
      textTransform: el.uppercase === false ? "none" : (el.fontSize >= 18 || el.letterSpacing ? "uppercase" : "none"),
      whiteSpace: "nowrap",
      lineHeight: 1.1,
      opacity: el.opacity ?? 1,
      color: cfg.card_text_color,
      pointerEvents: "none",
    }}>
      {text || <span style={{ opacity: 0.4 }}>(vacío)</span>}
    </div>
  );
}

/* ============================================================ */
/* SUBCOMPONENTS                                                */
/* ============================================================ */
const inputCls = "w-full bg-slate-50 border border-slate-200 px-3 h-10 text-slate-900 focus:border-[#2563EB] focus:bg-white outline-none placeholder:text-slate-400 text-sm";

function Section({ title, subtitle, icon: Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 p-4 shadow-card" style={{ borderRadius: 12 }}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={16} className="text-slate-400" />}
        <h3 className="font-display font-bold text-base uppercase tracking-tight">{title}</h3>
      </div>
      {subtitle && <p className="text-xs text-slate-500 -mt-2 mb-3">{subtitle}</p>}
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

function NumberField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 mb-1">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        className={inputCls}
      />
    </label>
  );
}

function SliderField({ label, icon: Icon, value, min, max, onChange }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 flex items-center gap-1">
          {Icon && <Icon size={12} />} {label}
        </span>
        <span className="text-xs font-bold text-slate-700 tabular-nums">{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full accent-[#2563EB]"
      />
    </label>
  );
}

function ColorPicker({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 mb-1">{label}</span>
      <div className="flex items-center gap-1.5 border border-slate-200 bg-slate-50 px-2 h-10" style={{ borderRadius: 8 }}>
        <input data-testid={testid} type="color" value={value} onChange={(e) => onChange(e.target.value)}
          className="h-7 w-8 border-0 bg-transparent cursor-pointer rounded" style={{ padding: 0 }} />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 bg-transparent outline-none text-xs font-mono text-slate-700" maxLength={9} />
      </div>
    </label>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between cursor-pointer py-1.5">
      <span className="text-sm text-slate-700">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 rounded-full transition-colors ${checked ? "bg-[#2563EB]" : "bg-slate-300"}`}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    </label>
  );
}

function LogoSlot({ label, url, onPick, onRemove, testid }) {
  return (
    <div className="border border-slate-200 bg-slate-50 p-2 flex flex-col items-center" style={{ borderRadius: 8 }}>
      <div className="h-14 w-full flex items-center justify-center bg-white border border-slate-200 mb-1 relative overflow-hidden" style={{ borderRadius: 6 }}>
        {url ? (
          <>
            <img src={url} alt="" className="max-h-full max-w-full object-contain p-1" data-testid={`${testid}-img`} />
            <button onClick={onRemove} type="button"
              className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-white/90 border border-slate-200 flex items-center justify-center text-slate-600 hover:text-red-600">
              <X size={10} weight="bold" />
            </button>
          </>
        ) : (
          <ImageIcon size={22} className="text-slate-300" />
        )}
      </div>
      <button type="button" onClick={onPick} data-testid={`${testid}-pick`}
        className="text-[10px] font-bold uppercase tracking-wide text-[#2563EB] hover:underline flex items-center gap-1">
        <UploadSimple size={11} /> {url ? "Cambiar" : label}
      </button>
    </div>
  );
}
